import { useRef, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  User,
  Camera,
  Trash2,
  Loader2,
  Mail,
  ShieldCheck
} from 'lucide-react';


export default function ParceiroPerfilPage() {

  const { user, setUser } = useAuth();

  const [uploading, setUploading] = useState(false);

  const fileRef = useRef(null);


  async function handleUpload(e) {

    const file = e.target.files?.[0];

    if (!file) return;

    const allowed = [
      'image/png',
      'image/jpeg',
      'image/jpg',
      'image/webp'
    ];

    if (!allowed.includes(file.type)) {
      toast.error(
        'Formato inválido. Permitidos: PNG, JPG, JPEG e WEBP'
      );
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error('Arquivo muito grande. Máximo: 2MB');
      return;
    }

    setUploading(true);

    try {

      const fd = new FormData();

      fd.append('file', file);

      const res = await api.post(
        '/auth/upload-avatar',
        fd,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setUser(prev => ({
        ...prev,
        avatar_url: res.data.avatar_url
      }));

      toast.success('Foto atualizada!');

    } catch (err) {

      toast.error(formatApiError(err));

    } finally {

      setUploading(false);

      if (fileRef.current) {
        fileRef.current.value = '';
      }

    }
  }


  async function handleRemove() {

    setUploading(true);

    try {

      await api.delete('/auth/upload-avatar');

      setUser(prev => ({
        ...prev,
        avatar_url: ''
      }));

      toast.success('Foto removida');

    } catch (err) {

      toast.error(
        formatApiError(err) ||
        'Erro ao remover foto'
      );

    } finally {

      setUploading(false);

    }
  }


  return (

    <div className="max-w-3xl mx-auto space-y-7">

      <div>

        <p className="text-xs font-bold tracking-[0.18em] uppercase text-slate-400">
          Minha conta
        </p>

        <h1 className="text-2xl sm:text-3xl font-bold text-slate-950 mt-1">
          Meu perfil
        </h1>

        <p className="text-sm text-slate-500 mt-2">
          Consulte as informações da sua conta de parceiro.
        </p>

      </div>


      {/* FOTO */}

      <div className="bg-white border border-slate-200 rounded-2xl p-5 sm:p-7">

        <div className="flex flex-col sm:flex-row sm:items-center gap-6">

          <div className="w-24 h-24 rounded-full border-2 border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden shrink-0">

            {user?.avatar_url ? (

              <img
                src={user.avatar_url}
                alt="Foto de perfil"
                className="w-full h-full object-cover"
              />

            ) : (

              <User className="w-10 h-10 text-slate-300" />

            )}

          </div>


          <div className="flex-1">

            <h2 className="text-lg font-bold text-slate-950">
              Foto de perfil
            </h2>

            <p className="text-sm text-slate-500 mt-1">
              PNG, JPG ou WEBP. Tamanho máximo de 2MB.
            </p>


            <div className="flex flex-wrap gap-2 mt-4">

              <input
                ref={fileRef}
                type="file"
                accept=".png,.jpg,.jpeg,.webp"
                onChange={handleUpload}
                className="hidden"
              />


              <Button
                type="button"
                disabled={uploading}
                onClick={() => fileRef.current?.click()}
                className="bg-slate-900 text-white hover:bg-slate-800 gap-2"
              >

                {uploading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Camera className="w-4 h-4" />
                )}

                {user?.avatar_url
                  ? 'Trocar foto'
                  : 'Enviar foto'}

              </Button>


              {user?.avatar_url && (

                <Button
                  type="button"
                  variant="outline"
                  disabled={uploading}
                  onClick={handleRemove}
                  className="border-slate-200 text-red-600 hover:bg-red-50 hover:text-red-700 gap-2"
                >
                  <Trash2 className="w-4 h-4" />
                  Remover
                </Button>

              )}

            </div>

          </div>

        </div>

      </div>


      {/* DADOS */}

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">

        <div className="px-5 sm:px-7 py-5 border-b border-slate-200">

          <h2 className="text-lg font-bold text-slate-950">
            Dados da conta
          </h2>

          <p className="text-sm text-slate-500 mt-1">
            Informações utilizadas no acesso ao portal.
          </p>

        </div>


        <div className="divide-y divide-slate-100">

          <div className="px-5 sm:px-7 py-5 flex items-center gap-4">

            <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center shrink-0">
              <User className="w-4 h-4 text-slate-600" />
            </div>

            <div className="min-w-0">

              <p className="text-xs text-slate-400">
                Nome
              </p>

              <p className="text-sm font-semibold text-slate-900 mt-0.5 truncate">
                {user?.name || '-'}
              </p>

            </div>

          </div>


          <div className="px-5 sm:px-7 py-5 flex items-center gap-4">

            <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center shrink-0">
              <Mail className="w-4 h-4 text-slate-600" />
            </div>

            <div className="min-w-0">

              <p className="text-xs text-slate-400">
                E-mail
              </p>

              <p className="text-sm font-semibold text-slate-900 mt-0.5 truncate">
                {user?.email || '-'}
              </p>

            </div>

          </div>


          <div className="px-5 sm:px-7 py-5 flex items-center gap-4">

            <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center shrink-0">
              <ShieldCheck className="w-4 h-4 text-slate-600" />
            </div>

            <div>

              <p className="text-xs text-slate-400">
                Tipo de acesso
              </p>

              <p className="text-sm font-semibold text-slate-900 mt-0.5">
                Parceiro
              </p>

            </div>

          </div>

        </div>

      </div>

    </div>
  );
}
